import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaCheckCircle, FaBox, FaCreditCard, FaTruck, FaMapMarkerAlt, FaCalendarAlt } from "react-icons/fa";
import confetti from "canvas-confetti";
import "./OrderConfirmation.css";

function useQuery() {
  return new URLSearchParams(window.location.search);
}

export default function OrderConfirmation() {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const query = useQuery();
  const orderId = query.get("order_id");

  useEffect(() => {
    if (!orderId) {
      setError("Missing order ID in URL.");
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      setLoading(true);
      setError("");

      try {
        const BASE_URL = process.env.REACT_APP_API_BASE || 'https://handmade-haven.in';
        const res = await fetch(`${BASE_URL}/api/customer/order-detail.php?order_id=${encodeURIComponent(orderId)}`, {
          method: "GET",
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });

        const text = await res.text();

        if (!text) throw new Error(`Empty response from API`);

        const trimmed = text.trim();
        if (trimmed.startsWith("<")) throw new Error(`Server returned HTML instead of JSON.`);

        let data;
        try {
          data = JSON.parse(trimmed);
        } catch (jsErr) {
          throw new Error(`Invalid JSON response: ${jsErr.message}`);
        }

        if (!data.ok) {
          const msg = data.error || data.message || `HTTP ${res.status}`;
          if (res.status === 401) throw new Error("Please log in to view this order.");
          throw new Error(msg);
        }

        if (!data.order) throw new Error('Invalid response format: missing order data');

        setOrder(data.order);

        // Trigger confetti animation on success
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#d14c87', '#ffb6c9', '#ff8fb3', '#f0a8d0']
        });
      } catch (err) {
        console.error("Order fetch error:", err);
        setError(err.message || "Failed to fetch order.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) return <div className="oc-container"><div className="oc-loading">Loading order…</div></div>;
  if (error) return <div className="oc-container"><div className="oc-error">Error: {error}</div></div>;
  if (!order) return <div className="oc-container"><div className="oc-error">Order not found.</div></div>;

  const items = order?.items ?? [];
  const orderNumber = order.id ?? order.order_id ?? order.orderId;
  const customerName = (order.customer_name ?? order.name) || 'Valued Customer';
  let paymentMethod = 'Cash on Delivery';
  if (order.razorpay_payment_id || order.payment_method === 'online') {
    paymentMethod = 'Razorpay';
  } else if (order.payment_method === 'pickup') {
    paymentMethod = 'Self Pickup (Pay at Pickup)';
  }
  const paymentStatus = order.payment_status === 'paid' ? 'Paid' : 'Pending';

  // Calculate estimated delivery (7-10 days from now)
  const today = new Date();
  const minDelivery = new Date(today);
  minDelivery.setDate(today.getDate() + 7);
  const maxDelivery = new Date(today);
  maxDelivery.setDate(today.getDate() + 10);

  const formatDate = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  return (
    <div className="oc-container">
      <motion.div
        className="oc-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Success Hero */}
        <motion.div
          className="oc-hero"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
        >
          <motion.div
            className="oc-success-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6, type: "spring", bounce: 0.5 }}
          >
            <FaCheckCircle />
          </motion.div>
          <h1 className="oc-hero-title">Order Successfully Placed!</h1>
          <p className="oc-hero-subtitle">Thank you for your purchase, {customerName}</p>
          <div className="oc-order-badge">
            Order #{orderNumber} • {paymentStatus === 'Paid' ? `₹${typeof order.total === 'number' ? order.total.toFixed(2) : order.total} Paid` : 'Payment Pending'}
          </div>
        </motion.div>

        {/* Info Cards Grid */}
        <motion.div
          className="oc-info-grid"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <div className="oc-info-card">
            <FaCalendarAlt className="oc-info-icon" />
            <div className="oc-info-content">
              <div className="oc-info-label">{order.payment_method === 'pickup' ? 'Pickup Date' : 'Estimated Delivery'}</div>
              <div className="oc-info-value">{order.payment_method === 'pickup' ? 'As arranged' : `${formatDate(minDelivery)}-${formatDate(maxDelivery)}`}</div>
            </div>
          </div>

          <div className="oc-info-card">
            <FaCreditCard className="oc-info-icon" />
            <div className="oc-info-content">
              <div className="oc-info-label">Payment</div>
              <div className="oc-info-value">
                {paymentMethod}
                {paymentStatus === 'Paid' && <span className="oc-paid-badge">✓ Paid</span>}
              </div>
            </div>
          </div>

          <div className="oc-info-card">
            <FaBox className="oc-info-icon" />
            <div className="oc-info-content">
              <div className="oc-info-label">Track Order</div>
              <button
                className="oc-track-btn"
                onClick={() => navigate(`/account/orders/${orderNumber}`)}
              >
                View Details
              </button>
            </div>
          </div>
        </motion.div>

        {/* Order Timeline */}
        <motion.div
          className="oc-timeline"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <div className="oc-timeline-step oc-timeline-active">
            <div className="oc-timeline-dot"></div>
            <div className="oc-timeline-label">Order Confirmed</div>
          </div>
          <div className="oc-timeline-line"></div>
          <div className="oc-timeline-step">
            <div className="oc-timeline-dot"></div>
            <div className="oc-timeline-label">Processing</div>
          </div>
          <div className="oc-timeline-line"></div>
          <div className="oc-timeline-step">
            <div className="oc-timeline-dot"></div>
            <div className="oc-timeline-label">Shipped</div>
          </div>
          <div className="oc-timeline-line"></div>
          <div className="oc-timeline-step">
            <div className="oc-timeline-dot"></div>
            <div className="oc-timeline-label">Delivered</div>
          </div>
        </motion.div>

        {/* Shipping Address */}
        <motion.div
          className="oc-section"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
        >
          <div className="oc-section-header">
            <FaMapMarkerAlt className="oc-section-icon" />
            <h3 className="oc-section-title">{order.payment_method === 'pickup' ? 'Customer Details' : 'Shipping Address'}</h3>
          </div>
          <div className="oc-address-box">
            <p className="oc-address-name">{customerName}</p>
            {order.phone && <p className="oc-address-detail">{order.phone}</p>}
            {order.email && <p className="oc-address-detail">{order.email}</p>}
            <p className="oc-address-detail oc-address-full">{order.address || '—'}</p>
          </div>
        </motion.div>

        {/* Order Items */}
        <motion.div
          className="oc-section"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <div className="oc-section-header">
            <FaTruck className="oc-section-icon" />
            <h3 className="oc-section-title">Order Items</h3>
          </div>
          {items.length > 0 ? (
            <div className="oc-items-list">
              {items.map((it, idx) => {
                const variantLabel = it?.variant_label || it?.variant;
                const itemName = variantLabel ? `${it.name} (${variantLabel})` : (it?.name || '—');
                const qty = it?.quantity ?? it?.qty ?? 1;
                const price = typeof it?.price === 'number' ? parseFloat(it.price) : parseFloat(it?.price || '0');
                const itemTotal = qty * price;

                return (
                  <div key={idx} className="oc-item">
                    {it.image && (
                      <img
                        src={it.image}
                        alt={it.name}
                        className="oc-item-image"
                      />
                    )}
                    <div className="oc-item-details">
                      <div className="oc-item-name">{itemName}</div>
                      <div className="oc-item-meta">Qty: {qty} • Unit Price: ₹{price.toFixed(2)}</div>
                    </div>
                    <div className="oc-item-price">₹{itemTotal.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="oc-no-items">No items found</p>
          )}
        </motion.div>

        {/* Order Summary */}
        <motion.div
          className="oc-summary"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
        >
          <h3 className="oc-summary-title">Payment Summary</h3>
          {order.subtotal !== undefined && order.shipping !== undefined ? (
            <>
              <div className="oc-summary-row">
                <span>Subtotal</span>
                <span>₹{typeof order.subtotal === 'number' ? order.subtotal.toFixed(2) : order.subtotal}</span>
              </div>
              <div className="oc-summary-row">
                <span>Shipping</span>
                <span>{order.shipping === 0 ? <span className="oc-free">FREE</span> : `₹${typeof order.shipping === 'number' ? order.shipping.toFixed(2) : order.shipping}`}</span>
              </div>
              <div className="oc-summary-divider"></div>
              <div className="oc-summary-row oc-summary-total">
                <span>{paymentStatus === 'Paid' ? 'Total Paid' : 'Total'}</span>
                <span>₹{typeof order.total === 'number' ? order.total.toFixed(2) : order.total}</span>
              </div>
            </>
          ) : (
            <div className="oc-summary-row oc-summary-total">
              <span>Total</span>
              <span>₹{typeof order.total === 'number' ? order.total.toFixed(2) : order.total}</span>
            </div>
          )}
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          className="oc-trust-badges"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
        >
          <div className="oc-badge-item">
            <span className="oc-badge-icon">🔒</span>
            <span className="oc-badge-text">100% Secure Payment</span>
          </div>
        </motion.div>

        {/* CTAs */}
        <motion.div
          className="oc-actions"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
        >
          <button
            className="oc-btn oc-btn-primary"
            onClick={() => navigate(`/account/orders/${orderNumber}`)}
          >
            View Order Details
          </button>
          <button
            className="oc-btn oc-btn-secondary"
            onClick={() => navigate('/products')}
          >
            Continue Shopping
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}